import React from 'react';
import LordIcon from './LordIcon';

/** Standard full-width field (text, email, textarea) */
export const fieldCls =
  'w-full h-8 px-2.5 border border-gray-200 rounded-[6px] text-[13px] leading-none text-black bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400';

/** Compact (numbers) */
export const compactCls =
  'w-auto max-w-full h-8 px-2.5 border border-gray-200 rounded-[6px] text-[13px] leading-none text-black bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500';

/** Selects - shrink to content */
export const selectCls =
  'w-max max-w-full h-8 px-2.5 border border-gray-200 rounded-[6px] text-[13px] leading-none text-black bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500';

type FormRowProps = {
  icon: string;
  label: string;
  children: React.ReactNode;
  align?: 'center' | 'start';
  /** Show red asterisk after label */
  required?: boolean;
  /**
   * `inline` - icon + label + `:` + control (slide-over)
   * `stacked` - icon + label on top, control below (register / pages)
   */
  layout?: 'inline' | 'stacked';
};

function LabelText({ label, required }: { label: string; required?: boolean }) {
  return (
    <span className="flex items-center gap-0.5 min-w-0">
      <span className="text-sm font-medium text-gray-600 truncate">{label}</span>
      {required && (
        <span className="text-red-500 shrink-0 text-sm font-medium" aria-hidden>
          *
        </span>
      )}
    </span>
  );
}

/** Icon + label + control */
const FormRow: React.FC<FormRowProps> = ({
  icon,
  label,
  children,
  align = 'center',
  required = false,
  layout = 'inline',
}) => {
  if (layout === 'stacked') {
    return (
      <div className="py-2">
        <div className="flex items-center gap-2 mb-1.5 min-w-0">
          <LordIcon src={icon} size={18} color="#4B5563" trigger="hover" />
          <LabelText label={label} required={required} />
        </div>
        <div className="min-w-0 w-full">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-[9.5rem_auto_1fr] gap-x-2 py-2 ${
        align === 'start' ? 'items-start' : 'items-center'
      }`}
    >
      <div className={`flex items-center gap-2 min-w-0 ${align === 'start' ? 'pt-2' : ''}`}>
        <LordIcon src={icon} size={18} color="#4B5563" trigger="hover" />
        <LabelText label={label} required={required} />
      </div>
      <span className={`text-gray-500 select-none ${align === 'start' ? 'pt-2' : ''}`}>:</span>
      <div className="min-w-0 flex items-center w-full">{children}</div>
    </div>
  );
};

export default FormRow;
